import React, { useState, useEffect } from 'react'
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tab
} from '@mui/material'
import { toast } from 'react-toastify'
import axiosIct from '../../utils/axios'
import './Support.scss'
import Modal from '../../components/Modal'
import { useModal } from '../../hooks/useModal'
import { useFormik, FormikErrors } from 'formik'
import Loader from '../../components/Loader/Loader'
import SelectDropdown from '../../components/SelectInput/SelectDropdown'
import RichTextEditor from '../../components/RichTextEditor/RichTextEditor'
import DragAndDropFile from '../../components/Input/DragAndDropFile'
import Input from '../../components/Input/Input'
import { Add } from '@mui/icons-material'
import { ChevronRight } from '@mui/icons-material'
import { ReactComponent as BugIcon } from '../../icons/support/bug.svg'
import { ReactComponent as HelpIcon } from '../../icons/support/help.svg'
import { ReactComponent as NewIcon } from '../../icons/support/newfeature.svg'
import { ReactComponent as OpenStatusIcon } from '../../icons/support/open.svg'
import { ReactComponent as ClosedStatusIcon } from '../../icons/support/closed.svg'
import { ReactComponent as InProgressIcon } from '../../icons/support/inprogress.svg'

type FormValues = {
  id?: string
  type: string
  product: string
  description: string
  title: string
  screenshot: File | null
  comments: string
}

type Ticket = {
  id: string
  name: string
  requestType: string
  status: string
  createdAt: string
  updatedAt: string
}

type TicketDetails = {
  id: string
  name: string
  notes: string
  status: string
  type: string
  product: string
  comments: { text: string; created_by: { name: string }; created_at: string }[]
}

const requestOptions = [

  { label:  <span className="status-container"><BugIcon className="request-icon" />Bug</span>, value: 'Bug' },
  { label:  <span className="status-container"><HelpIcon className="request-icon" />Help</span>, value: 'Help_Needed' },
  { label:  <span className="status-container"><NewIcon className="request-icon" />New Feature</span>, value: 'New_Feature' }
]

const productOptions = [
  { label: 'Product 1', value: 'Product_1' },
  { label: 'Product 2', value: 'Product_2' }
]

export default function Support(): React.ReactElement {
  const [loading, setLoading] = useState(false)
  const [openTickets, setOpenTickets] = useState<Ticket[]>([])
  const [closedTickets, setClosedTickets] = useState<Ticket[]>([])
  const [selectedTicket, setSelectedTicket] = useState<FormValues | null>(null)
  const [selectedTicketDetails, setSelectedTicketDetails] = useState<TicketDetails | null>(null)
  const [isUpdateMode, setIsUpdateMode] = useState(false)
  const [activeTab, setActiveTab] = useState(0) // 0 = Open Tickets, 1 = Closed Tickets
  const clientId: string = new URLSearchParams(location.search).get('Client') ?? ''
  const [productOptions, setProductOptions] = useState<any[]>([])

  const { isModalOpened, openModal, closeModal } = useModal()

  const fetchTickets = async (): Promise<void> => {
    setLoading(true)
    try {
      const response = await axiosIct.get(`/support/?Client=${clientId}`)
      const sortedTickets = response.data.sort(
        (a: Ticket, b: Ticket) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
      setOpenTickets(sortedTickets.filter((ticket: Ticket) => ( ticket.status === 'Open' || ticket.status === 'In Progress')))
      setClosedTickets(sortedTickets.filter((ticket: Ticket) => ticket.status === 'Closed'))
    } catch (error) {
      toast.error('Error fetching tickets', { theme: 'dark' })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTickets()
  }, [])

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  useEffect(() => { 
    const fetchClient  = async (): Promise<void> => {
      try {
        if (clientId != '' && clientId != null) {
        const response = await axiosIct.get(`/clients/${clientId}`)
        setProductOptions(response.data.products.map((product: string) => ({ label: product.toLocaleUpperCase(), value: product })))
        }
      } catch (error) {
        console.error('Error fetching client details:', error)
      } finally {
      }
    }
    void fetchClient()
    },[clientId])
  
  const formik = useFormik<FormValues>({
    initialValues: {
      id: selectedTicketDetails?.id || undefined,
      type: selectedTicketDetails?.type || 'Bug',
      product: selectedTicketDetails?.product || '',
      description: selectedTicketDetails?.notes || '',
      title: selectedTicketDetails?.name || '',
      screenshot: null,
      comments: ''
    },
    validate: (values) => {
      const errors: FormikErrors<FormValues> = {}

      if (!values.product.trim() && !isUpdateMode) errors.product = 'Product is required'
      if (!values.title.trim() && !isUpdateMode) errors.title = 'Title is required'
      if ((!values.description.trim() || values.description === '<p><br></p>') && !isUpdateMode) {
        errors.description = 'Description is required'
      }
      if( isUpdateMode && (!values.comments.trim() || values.comments === '<p><br></p>')) [
        errors.comments = 'Comments is required'
      ]
      return errors
    },
    onSubmit: async (values) => {
      setLoading(true)
      try {
        const formData = new FormData()

        if (!isUpdateMode) {
        formData.append('type', values.type)
        formData.append('product', values.product)
        formData.append('description', values.description)
        formData.append('title', values.title)
        formData.append('client_id', clientId)}
        {
          formData.append('comment', values.comments)
        }
        if (values.screenshot) {
          formData.append('screenshot', values.screenshot)
        }

        let response
        if (isUpdateMode) {
          formData.append('comment', values.comments)
          response = await axiosIct.put(`/support/${selectedTicketDetails?.id}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        } else {
          response = await axiosIct.post('/support/', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        }

        if (response.status === (isUpdateMode ? 200 : 201)) {
          toast.success(
            isUpdateMode ? '🎉 Ticket updated successfully!' : '🎉 Ticket created successfully!',
            { theme: 'dark' }
          )
          closeModal()
          fetchTickets()
        } else {
          toast.error('❌ Failed to process ticket.', { theme: 'dark' })
        }
      } catch (error) {
        toast.error('❌ Something went wrong. Please try again later.', { theme: 'dark' })
      } finally {
        setLoading(false)
      }
    },
    enableReinitialize: true
  })

  const getRequestTypeIcon = (type: string) => {
    switch (type) {
      case 'Bug': return <BugIcon className="request-icon" />
      case 'Help_Needed': return <HelpIcon className="request-icon" />
      case 'New_Feature': return <NewIcon className="request-icon" />
      default: return null
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Open': return <OpenStatusIcon className="status-icon" />
      case 'Closed': return <ClosedStatusIcon className="status-icon" />
      case 'In Progress': return <InProgressIcon className="status-icon-1" />
      default: return null
    }
  }
  const fetchTicketDetails = async (id: string) => {
    setLoading(true)
    try {
      const response = await axiosIct.get(`/support/${id}`)
      setSelectedTicketDetails(response.data)
    } catch (error) {
      toast.error('Error fetching ticket details', { theme: 'dark' })
    } finally {
      setLoading(false)
    }
  }

  const handleTicketClick = (id: string, event: React.MouseEvent<HTMLSpanElement>) => {
    event.stopPropagation() // Prevent triggering row click event
    fetchTicketDetails(id)
  }

  const handleOutsideClick = (event: MouseEvent) => {
    if ((event.target as HTMLElement).closest('.ticket-details-panel') === null) {
      setSelectedTicketDetails(null)
    }
  }

  useEffect(() => {
    if (selectedTicketDetails) {
      document.addEventListener('click', handleOutsideClick)
    } else {
      document.removeEventListener('click', handleOutsideClick)
    }
    return () => {
      document.removeEventListener('click', handleOutsideClick)
    }
  }, [selectedTicketDetails])

  const renderTicketTable = (tickets: Ticket[]) => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Ticket Name</TableCell>
            <TableCell>Request Type</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Created At</TableCell>
            <TableCell>Updated At</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tickets.map(ticket => (
            <TableRow key={ticket.id} className="ticket-row">
              <TableCell>
                {ticket.name}
                <span className="carrot-icon" onClick={(e) => handleTicketClick(ticket.id, e)}>
                  <ChevronRight />
                </span>
              </TableCell>
              <TableCell>
                <span className="request-container">
                  {getRequestTypeIcon(ticket.requestType)}
                  {ticket.requestType}
                </span>
              </TableCell>
              <TableCell>
                <span className="status-container">
                  {getStatusIcon(ticket.status)}
                </span>
              </TableCell>
              <TableCell>{new Date(ticket.createdAt).toLocaleString()}</TableCell>
              <TableCell>{new Date(ticket.updatedAt).toLocaleString()}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  )

  return (
    <>
      {loading && <Loader />}
      <div className="support d-flex flex-column">
        <div className="support-header d-flex align-items-baseline justify-content-between">
          <h3 className="overview-bold text--orange">Support</h3>
          <button className="btn--add-ticket" onClick={openModal}>
            Add New <Add className='icon--add-ticket' style={{ color: '#ffffff' }} />
          </button>
        </div>
        {/* Tabs with Styled Borders */}
        <div className="ticket-tabs-container">
          <Tabs value={activeTab} onChange={handleTabChange} className="ticket-tabs">
            <Tab label={`Open Tickets (${openTickets.length})`} className={activeTab === 0 ? 'active-tab' : ''} />
            <Tab label={`Closed Tickets (${closedTickets.length})`} className={activeTab === 1 ? 'active-tab' : ''} />
          </Tabs>
        </div>

        <div className={`ticket-table-container ${selectedTicketDetails ? 'ticket-details-open' : ''}`}>
          {activeTab === 0 ? renderTicketTable(openTickets) : renderTicketTable(closedTickets)}
        </div>

        {selectedTicketDetails && (
        <div className={`ticket-details-panel slide-in`}>
          <h3>{selectedTicketDetails.name}</h3>
          <p>{selectedTicketDetails.notes}</p>
          <h4>Comments</h4>
          {selectedTicketDetails.comments.map(comment => (
            <div key={comment.created_at} className="comment">
              <strong>{comment.created_by.name}</strong>
              <p>{comment.text}</p>
              <small>{new Date(comment.created_at).toLocaleString()}</small>
            </div>
          ))}
            <div className="comment-input-section">
              <label className="Input__label">Add Comment</label>
                  <RichTextEditor
                    text={formik.values.comments}
                    onChange={(content: string) => formik.setFieldValue('comments', content)}
              />
              <label className="Input__label">Screenshot</label>
              <DragAndDropFile
                onFileSelect={(file: File | null) => formik.setFieldValue('screenshot', file)}
              />
            {/* Button Now Calls formik.handleSubmit Directly */}
            <button 
              type="button" 
              className="save-btn" 
              disabled={formik.isSubmitting} 
              onClick={(e) => {
                setIsUpdateMode(true); // Ensure update mode is set
                formik.handleSubmit();  // Explicitly call submit
                e.stopPropagation();    // Prevent event bubbling
              }}
            >
              {formik.isSubmitting ? 'Updating...' : 'Update Ticket'}
            </button>
            </div>
        </div>
      )}
        <Modal
          isOpened={isModalOpened}
          closeModal={closeModal}
          title={isUpdateMode ? 'Update Ticket' : 'Create a New Support Ticket'}
        >
          <form className="form w-100" onSubmit={formik.handleSubmit}>
            <label className="Input__label">Request Type</label>
            <SelectDropdown
              options={requestOptions}
              value={formik.values.type}
              onChange={(value) => formik.setFieldValue('type', value)}
              disabled={isUpdateMode}
            />

            <label className="Input__label">Product</label>
            <SelectDropdown
              options={productOptions}
              value={formik.values.product}
              onChange={(value) => formik.setFieldValue('product', value)}
              disabled={isUpdateMode}
            />

            <Input
              name="title"
              label="Title"
              value={formik.values.title}
              onChange={formik.handleChange}
              error={formik.touched.title && formik.errors.title}
              readOnly={isUpdateMode}
            />

            <label className="Input__label">Description</label>
            <RichTextEditor
              text={formik.values.description}
              onChange={(content: string) => formik.setFieldValue('description', content)}
              readOnly={isUpdateMode}
            />

            <label className="Input__label">Screenshot</label>
            <DragAndDropFile
              onFileSelect={(file: File | null) => formik.setFieldValue('screenshot', file)}
            />

            <button type="submit" className="save-btn" disabled={formik.isSubmitting}>
              {formik.isSubmitting ? 'Submitting...' : 'Submit Ticket'}
            </button>
          </form>
        </Modal>
      </div>
    </>
  )
}
